import { create } from 'zustand';
import { apiClient } from '@/services/api';
import { useDemoStore } from '@/stores/demoStore';
import type { User } from '@/types';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitializing: boolean;
  error: string | null;
  setAuth: (user: User) => void;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  isInitializing: true, // Start as true to prevent premature redirects
  error: null,

  setAuth: (user: User) => {
    set({ user, isAuthenticated: true, isLoading: false, isInitializing: false, error: null });
  },

  logout: async () => {
    set({ isLoading: true });
    try {
      await apiClient.logout();
    } catch {
      // Even if logout fails, clear local state
    }
    if (useDemoStore.getState().isDemoMode) {
      useDemoStore.getState().exitDemo();
    }
    set({ user: null, isAuthenticated: false, isLoading: false, isInitializing: false });
  },

  checkAuth: async () => {
    if (get().isLoading) {
      return;
    }

    const { isInitializing } = get();
    // Full-page loader only on first boot; background rechecks stay quiet.
    if (isInitializing) {
      set({ isLoading: true });
    }
    try {
      const response = await apiClient.getCurrentUser();
      if (response.success && response.data) {
        // Verify user has librarian role
        if (response.data.role !== 'librarian') {
          console.error('User is not an admin');
          // Logout non-admin user
          await apiClient.logout();
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            isInitializing: false
          });
          return;
        }
        set({
          user: response.data,
          isAuthenticated: true,
          isLoading: false,
          isInitializing: false
        });
      } else {
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          isInitializing: false
        });
      }
    } catch {
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        isInitializing: false
      });
    }
  },

  clearError: () => set({ error: null }),
}));

